import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLexicon } from '../../hooks/useLexicon';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Loader from '../Loader';
import Toast from '../Toast';
import SchoolPlanModal from '../../components2/freemium/SchoolPlanModal';
import StudentPlanUpgradePopup from '../../components2/freemium/StudentPlanUpgradePopup';
import { AppContext } from '../../app.context';
import {
  getAdvancedMarkingClassOverrides,
  getAdvancedMarkingStudentSettings,
  getAdvancedMarkingTeacherSettings,
  getClassSettingForClass,
  getProfile,
  deleteAdvancedMarkingClassOverride,
  updateAdvancedMarkingClassOverride,
  updateClassSettingForClass,
  updateAdvancedMarkingStudentSettings,
  updateAdvancedMarkingTeacherSettings,
} from '../../service';
import { useActiveClassData } from '../state/hooks';
import {
  ADVANCED_MARKING_DEFAULTS,
  FEEDBACK_AMOUNT_DEFAULT,
  normalizeStudentFeedbackAmount,
  stripNullValues,
} from '../../utils/advancedMarking';
import {
  FREE_MONTHLY_CREDITS,
  getCurrentPlan,
  getUpgradeOptions,
  hasTeacherSku,
  isExpertRole,
  isStudentRole,
  isTeacherRole,
} from '../../utils/userAccessHelper';
import {
  AdvancedMarkingControlsForm,
  FeedbackAmountControl,
} from './ControlsForm';
import {
  ActionRow,
  ClassCard,
  ClassHeader,
  ClassTitle,
  ClassesList,
  Content,
  EmptyState,
  Header,
  HeaderTextGroup,
  MainContainer,
  NoticeCard,
  OuterContainer,
  DependentToggles,
  PrimaryButton,
  SecondaryButton,
  Section,
  SubTitle,
  TabButton,
  Tabs,
  ToggleDescription,
  ToggleLabelGroup,
  ToggleRow,
  ToggleTitle,
  Title,
} from './style';
import ImprovedSecondarySideBar from '../ImprovedSecondarySideBar';
import MenuButton from '../MenuButton';
import { isTabletView } from '../ReactiveRender';
import ToggleSwitchWithOneOption from '../../components2/ToggleSwitchWithOneOption';

const TAB_GLOBAL = 'GLOBAL';
const TAB_CLASSES = 'CLASSES';

const areClassSettingsMapsEqual = (left = {}, right = {}) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => {
    // Same size is not same keys: maps over different class IDs must differ,
    // or a stale entry could survive a class-list change.
    if (!Object.hasOwn(right, key)) {
      return false;
    }
    const leftSetting = left[key] || {};
    const rightSetting = right[key] || {};

    return (
      String(leftSetting.id || key) === String(rightSetting.id || key) &&
      // Raw comparison: undefined means "fetch failed / not read", which must
      // NOT compare equal to an explicit false — otherwise a later successful
      // read of a hard-disabled class never replaces the failed placeholder.
      leftSetting.jeddAIEnabled === rightSetting.jeddAIEnabled &&
      (leftSetting.studentJeddAIEnabled !== false) ===
        (rightSetting.studentJeddAIEnabled !== false) &&
      Boolean(leftSetting.rubricEnabled) === Boolean(rightSetting.rubricEnabled)
    );
  });
};

const createGlobalStateSafe = (queryKey, queryFunction) => {
  return function useGlobalStateSafe(
    id = null,
    condition = true,
    time = 300_000
  ) {
    const queryClient = useQueryClient();
    const fullQueryKey = id ? [queryKey, id] : [queryKey];
    const { data, isLoading, isFetching, isError, error } = useQuery({
      queryKey: fullQueryKey,
      queryFn: async () => {
        return id ? queryFunction(id) : queryFunction();
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      keepPreviousData: true,
      enabled: condition,
      staleTime: time,
      cacheTime: time,
    });

    const isLoadingdata = isLoading;

    function setData(nextData) {
      queryClient.setQueryData(fullQueryKey, nextData);
    }

    function resetData() {
      queryClient.invalidateQueries({ queryKey: fullQueryKey });
      queryClient.refetchQueries({ queryKey: fullQueryKey });
    }

    return {
      data,
      isLoadingdata,
      isFetching,
      isError,
      error,
      setData,
      resetData,
    };
  };
};

// Round 7 follow-up: local fetchClassWithStudentsAndTeachers + local useClassData
// removed. This component now consumes the shared useActiveClassData hook from
// components/state/hooks, which reads from the batched /classes/with-members
// endpoint (one HTTP call instead of 1+C). Two benefits beyond perf:
//   (a) we no longer have two queryFns under the same React Query 'classData'
//       cache key — the prior local copy collided with the shared hook and
//       whichever ran first won until the next invalidate.
//   (b) the degraded-flag plumbing the shared hook already does (warn-only on
//       partial WP responses) is now applied to this surface too.
const fetchProfileSafe = async () => {
  if (typeof getProfile !== 'function') {
    return null;
  }
  return getProfile();
};

const fetchAdvancedTeacherSettingsSafe = async () => {
  if (typeof getAdvancedMarkingTeacherSettings !== 'function') {
    return {};
  }
  return getAdvancedMarkingTeacherSettings();
};

const fetchAdvancedClassOverridesSafe = async () => {
  if (typeof getAdvancedMarkingClassOverrides !== 'function') {
    return [];
  }
  return getAdvancedMarkingClassOverrides();
};

const fetchAdvancedStudentSettingsSafe = async () => {
  if (typeof getAdvancedMarkingStudentSettings !== 'function') {
    return { feedbackAmount: FEEDBACK_AMOUNT_DEFAULT };
  }
  return getAdvancedMarkingStudentSettings();
};

const useProfileData = createGlobalStateSafe('profile', fetchProfileSafe);
const useAdvancedMarkingTeacherSettings = createGlobalStateSafe(
  'advancedMarkingTeacherSettings',
  fetchAdvancedTeacherSettingsSafe
);
const useAdvancedMarkingClassOverrides = createGlobalStateSafe(
  'advancedMarkingClassOverrides',
  fetchAdvancedClassOverridesSafe
);
const useAdvancedMarkingStudentSettings = createGlobalStateSafe(
  'advancedMarkingStudentSettings',
  fetchAdvancedStudentSettingsSafe
);

const getJeddaiRoleFallback = (profile) => {
  return profile?.permissions?.jeddaiRole;
};
const isTeacherRoleFallback = (profile) => {
  const jeddaiRole = getJeddaiRoleFallback(profile);
  if (!jeddaiRole) return false;
  return String(jeddaiRole).toUpperCase() === 'TEACHER';
};
const isStudentRoleFallback = (profile) => {
  const jeddaiRole = getJeddaiRoleFallback(profile);
  return Boolean(jeddaiRole) && String(jeddaiRole).toUpperCase() === 'STUDENT';
};
const hasTeacherSkuFallback = (profile) => {
  const sku = profile?.subscriptionDetails?.sku || '';
  return String(sku).toUpperCase().includes('JAI-TEA');
};
const isExpertRoleFallback = (profile) => {
  return (
    isTeacherRoleFallback(profile) &&
    profile?.permissions?.hasClasses !== true &&
    !hasTeacherSkuFallback(profile)
  );
};
const getUpgradeOptionsFallback = (profile) => {
  return profile?.permissions?.upgradeOptions || [];
};
const getCurrentPlanFallback = (profile) => {
  const sku = profile?.subscriptionDetails?.sku || '';
  if (sku.includes('FREE')) return 'free';
  if (sku.includes('PRO')) return 'pro';
  if (sku.includes('SCHOOL')) return 'school';
  if (sku.includes('PLUS')) return 'plus';
  return 'free';
};

const AdvancedMarkingControls = () => {
  const [isShowMenu, setShowMenu] = useState(false);
  const { t } = useLexicon();
  const tabletView =
    typeof isTabletView === 'function' ? isTabletView() : false;
  const { data: profile, isLoadingdata: isLoadingProfile } = useProfileData();
  const isTeacher =
    typeof isTeacherRole === 'function'
      ? isTeacherRole(profile)
      : isTeacherRoleFallback(profile);
  const isStudent =
    typeof isStudentRole === 'function'
      ? isStudentRole(profile)
      : isStudentRoleFallback(profile);
  const teacherHasSku =
    typeof hasTeacherSku === 'function'
      ? hasTeacherSku(profile)
      : hasTeacherSkuFallback(profile);
  const hasClasses = profile?.permissions?.hasClasses === true;
  const isSchoolTeacher = isTeacher && hasClasses;
  const isSchoolStudent = isStudent && hasClasses;
  const isNonSchoolTeacher = isTeacher && !hasClasses && teacherHasSku;
  const isNonSchoolStudent = isStudent && !hasClasses;
  const isExpert =
    typeof isExpertRole === 'function'
      ? isExpertRole(profile)
      : isExpertRoleFallback(profile);
  const isTeacherAccess = isTeacher && !isExpert;
  // Any teacher on a JeddAI teacher plan — free (JAI-TEA-FREE) or paid
  // (JAI-TEA-PRO) — can read and save their own global marking preferences.
  // The backend /advanced-marking/teacher endpoints accept school and
  // non-school teachers alike; only class overrides stay school-scoped because
  // they require classes. Experts (no teacher plan) remain excluded — the
  // backend 403s them.
  const shouldLoadTeacherSettings = isTeacherAccess;
  const shouldLoadClassOverrides = isSchoolTeacher;
  const shouldLoadClassData = isSchoolTeacher;
  const shouldLoadStudentSettings = isStudent;
  const canPersistTeacherSettings = isTeacherAccess;
  const upgradeOptions =
    typeof getUpgradeOptions === 'function'
      ? getUpgradeOptions(profile)
      : getUpgradeOptionsFallback(profile);
  const currentPlan =
    typeof getCurrentPlan === 'function'
      ? getCurrentPlan(profile)
      : getCurrentPlanFallback(profile);
  const canUpdateTeacherSettings =
    typeof updateAdvancedMarkingTeacherSettings === 'function';
  const canUpdateClassOverride =
    typeof updateAdvancedMarkingClassOverride === 'function';
  const canDeleteClassOverride =
    typeof deleteAdvancedMarkingClassOverride === 'function';
  const canUpdateStudentSettings =
    typeof updateAdvancedMarkingStudentSettings === 'function';
  const canUpdateClassSettings =
    typeof updateClassSettingForClass === 'function';

  const { activateSidebarOverlay, deactivateSidebarOverlay } =
    useContext(AppContext) || {};

  // Shared useActiveClassData (components/state/hooks) reads from the
  // batched /classes/with-members endpoint AND filters to active classes
  // — same semantic as the prior local fan-out via
  // getActiveClassesWithStudents. Passing shouldLoadClassData as the
  // condition arg keeps the load gate identical (students/non-school
  // teachers skip the fetch entirely).
  const { data: classData, isLoadingdata: isLoadingClasses } =
    useActiveClassData(null, shouldLoadClassData);

  const {
    data: teacherSettings,
    isLoadingdata: isLoadingTeacherSettings,
    resetData: resetTeacherSettings,
  } = useAdvancedMarkingTeacherSettings(null, shouldLoadTeacherSettings);
  const {
    data: classOverrides,
    isLoadingdata: isLoadingClassOverrides,
    resetData: resetClassOverrides,
  } = useAdvancedMarkingClassOverrides(null, shouldLoadClassOverrides);
  const {
    data: studentSettings,
    isLoadingdata: isLoadingStudentSettings,
    resetData: resetStudentSettings,
  } = useAdvancedMarkingStudentSettings(null, shouldLoadStudentSettings);

  const [activeTab, setActiveTab] = useState(TAB_GLOBAL);
  const [globalControls, setGlobalControls] = useState(
    ADVANCED_MARKING_DEFAULTS
  );
  const [classControlsById, setClassControlsById] = useState({});
  const [savedClassControlsById, setSavedClassControlsById] = useState({});
  const [classSettingsById, setClassSettingsById] = useState({});
  const [feedbackAmount, setFeedbackAmount] = useState(FEEDBACK_AMOUNT_DEFAULT);
  const [isSchoolPlanModalOpen, setIsSchoolPlanModalOpen] = useState(false);
  const [isStudentUpgradeOpen, setIsStudentUpgradeOpen] = useState(false);

  useEffect(() => {
    if (teacherSettings) {
      setGlobalControls({
        ...ADVANCED_MARKING_DEFAULTS,
        ...stripNullValues(teacherSettings),
      });
    }
  }, [teacherSettings]);

  useEffect(() => {
    if (!Array.isArray(classOverrides)) return;
    const next = classOverrides.reduce((acc, override) => {
      if (override?.classId) {
        acc[override.classId] = {
          ...ADVANCED_MARKING_DEFAULTS,
          ...stripNullValues(override.controls || {}),
        };
      }
      return acc;
    }, {});
    setClassControlsById(next);
    setSavedClassControlsById(next);
  }, [classOverrides]);

  useEffect(() => {
    if (studentSettings?.feedbackAmount) {
      setFeedbackAmount(
        normalizeStudentFeedbackAmount(studentSettings.feedbackAmount)
      );
    }
  }, [studentSettings]);

  const classList = useMemo(
    () => (shouldLoadClassData && Array.isArray(classData) ? classData : []),
    [classData, shouldLoadClassData]
  );
  const classIdsForSettings = useMemo(
    () => classList.map((clazz) => clazz?.id).filter(Boolean),
    [classList]
  );
  const shouldLoadClassSettings =
    isSchoolTeacher &&
    classIdsForSettings.length > 0 &&
    typeof getClassSettingForClass === 'function';

  const {
    data: classSettingsData = [],
    isLoading: isLoadingClassSettings,
    refetch: refetchClassSettings,
  } = useQuery({
    queryKey: ['jeddaiClassSettings', classIdsForSettings],
    queryFn: async () => {
      if (typeof getClassSettingForClass !== 'function') {
        return [];
      }
      return Promise.all(
        classIdsForSettings.map(async (classId) => {
          try {
            const setting = await getClassSettingForClass(classId);
            return { id: classId, ...(setting || {}) };
          } catch (error) {
            return { id: classId };
          }
        })
      );
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
    enabled: shouldLoadClassSettings,
    staleTime: 300_000,
    cacheTime: 300_000,
  });
  const serverClassSettingsById = useMemo(() => {
    return Array.isArray(classSettingsData)
      ? classSettingsData.reduce((acc, setting) => {
          if (setting?.id) {
            acc[setting.id] = setting;
          }
          return acc;
        }, {})
      : {};
  }, [classSettingsData]);

  useEffect(() => {
    setClassSettingsById((prev) => {
      // A failed per-class fetch yields a placeholder without jeddAIEnabled
      // (a successful read always carries it). Never let such a placeholder
      // overwrite a previously known-good setting — only first-load failures
      // (no prior real value) may surface as placeholders.
      const merged = Object.fromEntries(
        Object.entries(serverClassSettingsById).map(([classId, setting]) => [
          classId,
          setting?.jeddAIEnabled === undefined &&
          prev[classId]?.jeddAIEnabled !== undefined
            ? prev[classId]
            : setting,
        ])
      );
      return areClassSettingsMapsEqual(prev, merged) ? prev : merged;
    });
  }, [serverClassSettingsById]);

  const showLoader =
    isLoadingProfile ||
    (shouldLoadClassData && isLoadingClasses) ||
    (shouldLoadTeacherSettings && isLoadingTeacherSettings) ||
    (shouldLoadClassOverrides && isLoadingClassOverrides) ||
    (shouldLoadClassSettings && isLoadingClassSettings) ||
    (shouldLoadStudentSettings && isLoadingStudentSettings);

  const openSchoolPlanModal = () => {
    setIsSchoolPlanModalOpen(true);
  };

  const openStudentUpgrade = () => {
    if (!isStudentUpgradeOpen && activateSidebarOverlay) {
      activateSidebarOverlay();
    }
    setIsStudentUpgradeOpen(true);
  };

  const closeStudentUpgrade = () => {
    if (isStudentUpgradeOpen && deactivateSidebarOverlay) {
      deactivateSidebarOverlay();
    }
    setIsStudentUpgradeOpen(false);
  };

  const handleResetGlobalDefaults = () => {
    setGlobalControls(ADVANCED_MARKING_DEFAULTS);
  };

  const handleResetClassDefaults = (classId) => {
    setClassControlsById((prev) => ({
      ...prev,
      [classId]: { ...ADVANCED_MARKING_DEFAULTS },
    }));
  };

  const setClassSettingInLocalState = (classId, setting) => {
    setClassSettingsById((prev) => {
      const next = { ...prev };
      if (setting && Object.keys(setting).length > 0) {
        next[classId] = { id: classId, ...setting };
      } else {
        delete next[classId];
      }
      return next;
    });
  };

  const removeClassOverrideFromLocalState = (
    classId,
    clearSavedOverride = false
  ) => {
    setClassControlsById((prev) => {
      const next = { ...prev };
      delete next[classId];
      return next;
    });

    if (clearSavedOverride) {
      setSavedClassControlsById((prev) => {
        const next = { ...prev };
        delete next[classId];
        return next;
      });
    }
  };

  const handleGlobalChange = (updates) => {
    setGlobalControls((prev) => ({ ...prev, ...updates }));
  };

  const handleSaveGlobal = async () => {
    if (!canPersistTeacherSettings) {
      openSchoolPlanModal();
      return;
    }
    if (!canUpdateTeacherSettings) {
      toast(<Toast message="Unable to save global settings" />);
      return;
    }
    try {
      await updateAdvancedMarkingTeacherSettings(globalControls);
      resetTeacherSettings();
      toast(<Toast message="Global settings updated" />);
    } catch (error) {
      toast(<Toast message="Unable to save global settings" />);
    }
  };

  const handleToggleClassOverride = (classId, enabled) => {
    if (enabled) {
      setClassControlsById((prev) => ({
        ...prev,
        [classId]: {
          ...ADVANCED_MARKING_DEFAULTS,
          ...globalControls,
          ...(prev[classId] || savedClassControlsById[classId] || {}),
        },
      }));
    } else {
      // JED-845: toggling the override OFF must actually cancel a SAVED override
      // on the backend, not just collapse the form locally (which silently
      // reappears on reload). Route through the same path as "Remove override".
      if (savedClassControlsById[classId]) {
        handleRemoveClassOverride(classId);
      } else {
        removeClassOverrideFromLocalState(classId);
      }
    }
  };

  const handleClassControlsChange = (classId, updates) => {
    setClassControlsById((prev) => ({
      ...prev,
      [classId]: {
        ...ADVANCED_MARKING_DEFAULTS,
        ...globalControls,
        ...(prev[classId] || {}),
        ...updates,
      },
    }));
  };

  const handleSaveClassOverride = async (classId) => {
    if (!canPersistTeacherSettings) {
      openSchoolPlanModal();
      return;
    }
    const hasOverride = Boolean(classControlsById[classId]);
    const hadOverride = Boolean(savedClassControlsById[classId]);
    try {
      if (hasOverride) {
        if (!canUpdateClassOverride) {
          toast(<Toast message="Unable to update class override" />);
          return;
        }
        await updateAdvancedMarkingClassOverride(
          classId,
          classControlsById[classId]
        );
        toast(<Toast message="Class override saved" />);
      } else if (hadOverride) {
        if (!canDeleteClassOverride) {
          toast(<Toast message="Unable to update class override" />);
          return;
        }
        await deleteAdvancedMarkingClassOverride(classId);
        toast(<Toast message="Class override removed" />);
      }
      resetClassOverrides();
    } catch (error) {
      toast(<Toast message="Unable to update class override" />);
    }
  };

  const handleRemoveClassOverride = async (classId) => {
    if (!canPersistTeacherSettings) {
      openSchoolPlanModal();
      return;
    }
    if (!canDeleteClassOverride) {
      toast(<Toast message="Unable to remove class override" />);
      return;
    }
    const hadOverride = Boolean(savedClassControlsById[classId]);
    if (!hadOverride) {
      removeClassOverrideFromLocalState(classId);
      return;
    }
    try {
      await deleteAdvancedMarkingClassOverride(classId);
      removeClassOverrideFromLocalState(classId, true);
      resetClassOverrides();
      toast(<Toast message="Class override removed" />);
    } catch (error) {
      toast(<Toast message="Unable to remove class override" />);
    }
  };

  const handleSaveStudent = async () => {
    if (!canUpdateStudentSettings) {
      toast(<Toast message="Unable to save feedback preference" />);
      return;
    }
    try {
      await updateAdvancedMarkingStudentSettings(feedbackAmount);
      resetStudentSettings();
      toast(<Toast message="Draft feedback updated" />);
    } catch (error) {
      toast(<Toast message="Unable to save feedback preference" />);
    }
  };

  const handleJeddAIClassSettingChange =
    (classId, toggleType) => async (event) => {
      if (!canUpdateClassSettings) {
        toast(<Toast message="Unable to update JeddAI settings" />);
        return;
      }

      const { checked } = event.target;
      const currentSetting = classSettingsById[classId] || {};
      // A successful settings read always carries jeddAIEnabled (the backend
      // synthesizes defaults for missing docs); if it's absent here the fetch
      // for this class failed. The PUT replaces the whole document, so writing
      // a guessed admin value could overwrite a real DB jeddAIEnabled=false —
      // refuse instead of guessing.
      if (currentSetting?.jeddAIEnabled === undefined) {
        toast(<Toast message="Unable to update JeddAI settings" />);
        return;
      }
      const shouldDisableDependents =
        toggleType === 'studentJeddAIEnabled' && !checked;
      const hasSavedOverride = Boolean(savedClassControlsById[classId]);
      const hasLocalOverride = Boolean(classControlsById[classId]);
      const previousSetting = currentSetting ? { ...currentSetting } : {};
      const previousClassControlsById = classControlsById;
      const previousSavedClassControlsById = savedClassControlsById;

      if (
        shouldDisableDependents &&
        hasSavedOverride &&
        !canDeleteClassOverride
      ) {
        toast(<Toast message="Unable to update JeddAI settings" />);
        return;
      }

      // The teacher toggle governs studentJeddAIEnabled only. jeddAIEnabled is
      // the admin kill-switch (set directly in the DB) — pass the value we
      // actually read through unchanged so a teacher save can't flip it: the
      // PUT replaces the whole settings document.
      const updatedClassSetting =
        toggleType === 'studentJeddAIEnabled'
          ? {
              id: classId,
              jeddAIEnabled: currentSetting.jeddAIEnabled,
              studentJeddAIEnabled: checked,
              rubricEnabled: checked,
            }
          : {
              id: classId,
              jeddAIEnabled: currentSetting.jeddAIEnabled,
              studentJeddAIEnabled: currentSetting.studentJeddAIEnabled,
              rubricEnabled: checked,
            };

      setClassSettingInLocalState(classId, updatedClassSetting);
      if (shouldDisableDependents) {
        removeClassOverrideFromLocalState(classId, hasSavedOverride);
      }

      try {
        await updateClassSettingForClass(classId, updatedClassSetting);
      } catch (error) {
        setClassSettingInLocalState(classId, previousSetting);
        setClassControlsById(previousClassControlsById);
        setSavedClassControlsById(previousSavedClassControlsById);
        toast(<Toast message="Unable to update JeddAI settings" />);
        return;
      }

      if (shouldDisableDependents && hasSavedOverride) {
        try {
          await deleteAdvancedMarkingClassOverride(classId);
          resetClassOverrides();
        } catch (error) {
          resetClassOverrides();
          toast(<Toast message="Unable to update JeddAI settings" />);
        }
      }

      void refetchClassSettings();
    };

  if (showLoader) {
    return (
      <OuterContainer>
        {ImprovedSecondarySideBar ? (
          <ImprovedSecondarySideBar
            isShowMenu={isShowMenu}
            setShowMenu={setShowMenu}
          />
        ) : null}
        <MainContainer>
          <Loader />
        </MainContainer>
      </OuterContainer>
    );
  }

  const pageTitle = isStudent
    ? 'My Draft feedback preferences'
    : t('Your marking preferences');
  const pageSubtitle = isStudent
    ? 'Choose how much feedback you want in My Drafts. This does not change teacher-assigned task settings.'
    : t('Set your marking preferences to help JeddAI tailor feedback to your style');

  return (
    <OuterContainer>
      {ImprovedSecondarySideBar ? (
        <ImprovedSecondarySideBar
          isShowMenu={isShowMenu}
          setShowMenu={setShowMenu}
        />
      ) : null}
      <MainContainer>
        {tabletView && MenuButton ? (
          <div style={{ padding: '16px 20px 0' }}>
            <MenuButton setShowMenu={setShowMenu} />
          </div>
        ) : null}
        <Header>
          <HeaderTextGroup>
            <Title>{pageTitle}</Title>
            <SubTitle>{pageSubtitle}</SubTitle>
          </HeaderTextGroup>
          {isTeacherAccess && activeTab === TAB_GLOBAL && (
            <PrimaryButton onClick={handleSaveGlobal}>
              Save Preferences
            </PrimaryButton>
          )}
        </Header>
        <Content>
          {isExpert && (
            <NoticeCard>
              <strong>Unavailable for expert accounts:</strong>{' '}
              {t('Marking preferences are only available to school or teacher plan users.')}
            </NoticeCard>
          )}
          {isNonSchoolTeacher && (
            <NoticeCard>
              <strong>Tip:</strong>{' '}
              {t('These marking preferences apply to all your JeddAI feedback. Per-class overrides unlock when your school adds you to classes.')}
            </NoticeCard>
          )}
          {isNonSchoolStudent && currentPlan === 'free' && (
            <NoticeCard>
              <strong>Free plan notice:</strong> Your free plan includes{' '}
              {FREE_MONTHLY_CREDITS} credits every month (1 credit covers up to 750 words). Upgrade for unlimited feedback.
              <ActionRow>
                <PrimaryButton onClick={openStudentUpgrade}>
                  See upgrade options
                </PrimaryButton>
              </ActionRow>
            </NoticeCard>
          )}
          {isTeacherAccess && (
            <Tabs>
              <TabButton
                isActive={activeTab === TAB_GLOBAL}
                onClick={() => setActiveTab(TAB_GLOBAL)}
              >
                Global
              </TabButton>
              <TabButton
                isActive={activeTab === TAB_CLASSES}
                onClick={() => setActiveTab(TAB_CLASSES)}
              >
                Classes
              </TabButton>
            </Tabs>
          )}

          {isTeacherAccess && activeTab === TAB_GLOBAL && (
            <Section>
              <AdvancedMarkingControlsForm
                controls={globalControls}
                onChange={handleGlobalChange}
              />
              <ActionRow style={{ justifyContent: 'center' }}>
                <PrimaryButton onClick={handleSaveGlobal}>
                  Save Preferences
                </PrimaryButton>
                <SecondaryButton onClick={handleResetGlobalDefaults}>
                  Reset Defaults
                </SecondaryButton>
              </ActionRow>
            </Section>
          )}

          {isTeacherAccess && activeTab === TAB_CLASSES && (
            <Section>
              {classList.length === 0 && (
                <EmptyState>No classes are available yet.</EmptyState>
              )}
              {classList.length > 0 && (
                <ClassesList>
                  {classList.map((clazz) => {
                    const hasOverride = Boolean(classControlsById[clazz.id]);
                    const hasSavedOverride = Boolean(
                      savedClassControlsById[clazz.id]
                    );
                    const setting = classSettingsById[clazz.id] || {};
                    // JED-1148: jeddAIEnabled is the admin kill-switch (set in
                    // the DB, never by this screen). While it is off, JeddAI
                    // stays off for the class whatever the teacher toggle
                    // says, so show the class as off and lock the toggle
                    // rather than a switch that reads ON and changes nothing.
                    const isJeddAIOffByAdmin = setting?.jeddAIEnabled === false;
                    // Teacher-facing toggle state: the student-facing flag
                    // (null/missing = enabled).
                    const isJeddAIEnabled =
                      !isJeddAIOffByAdmin &&
                      setting?.studentJeddAIEnabled !== false;
                    const isClassOverrideEnabled =
                      isJeddAIEnabled && hasOverride;
                    const controls = hasOverride
                      ? {
                          ...ADVANCED_MARKING_DEFAULTS,
                          ...globalControls,
                          ...classControlsById[clazz.id],
                        }
                      : globalControls;
                    const classTitle = clazz.title || clazz.name || 'Class';
                    return (
                      <ClassCard key={clazz.id}>
                        <ClassHeader>
                          <ClassTitle>{classTitle}</ClassTitle>
                        </ClassHeader>
                        <ToggleRow>
                          <ToggleLabelGroup>
                            <ToggleTitle>
                              Enable JeddAI for students
                            </ToggleTitle>
                            <ToggleDescription>
                              {isJeddAIOffByAdmin
                                ? "JeddAI has been turned off for this class by an administrator, so it can't be turned on here."
                                : "Allow JeddAI feedback for this class's students."}
                            </ToggleDescription>
                          </ToggleLabelGroup>
                          <ToggleSwitchWithOneOption
                            onChecked={isJeddAIEnabled}
                            disabled={isJeddAIOffByAdmin}
                            ariaLabel={`Enable JeddAI for ${classTitle}`}
                            onChangeFn={handleJeddAIClassSettingChange(
                              clazz.id,
                              'studentJeddAIEnabled'
                            )}
                          />
                        </ToggleRow>
                        <DependentToggles>
                          <ToggleRow
                            style={{
                              opacity: isJeddAIEnabled ? 1 : 0.5,
                            }}
                          >
                            <ToggleLabelGroup>
                              <ToggleTitle>Enable class override</ToggleTitle>
                              <ToggleDescription>
                                {t('Use custom marking preferences for this class.')}
                              </ToggleDescription>
                            </ToggleLabelGroup>
                            <ToggleSwitchWithOneOption
                              onChecked={isClassOverrideEnabled}
                              disabled={!isJeddAIEnabled}
                              ariaLabel={`Enable class override for ${classTitle}`}
                              onChangeFn={(event) =>
                                handleToggleClassOverride(
                                  clazz.id,
                                  event.target.checked
                                )
                              }
                            />
                          </ToggleRow>
                          <ToggleRow
                            style={{
                              opacity: isJeddAIEnabled ? 1 : 0.5,
                            }}
                          >
                            <ToggleLabelGroup>
                              <ToggleTitle>
                                Rubric marking for students
                              </ToggleTitle>
                              <ToggleDescription>
                                Show rubric-based marking to students in this
                                class.
                              </ToggleDescription>
                            </ToggleLabelGroup>
                            <ToggleSwitchWithOneOption
                              onChecked={
                                isJeddAIEnabled &&
                                Boolean(setting?.rubricEnabled)
                              }
                              disabled={!isJeddAIEnabled}
                              ariaLabel={`Enable rubric marking for ${classTitle}`}
                              onChangeFn={handleJeddAIClassSettingChange(
                                clazz.id,
                                'rubricEnabled'
                              )}
                            />
                          </ToggleRow>
                        </DependentToggles>
                        {isClassOverrideEnabled ? (
                          <>
                            <AdvancedMarkingControlsForm
                              controls={controls}
                              onChange={(updates) =>
                                handleClassControlsChange(clazz.id, updates)
                              }
                            />
                            <ActionRow>
                              <PrimaryButton
                                onClick={() =>
                                  handleSaveClassOverride(clazz.id)
                                }
                              >
                                Save Preferences
                              </PrimaryButton>
                              <SecondaryButton
                                onClick={() =>
                                  handleResetClassDefaults(clazz.id)
                                }
                              >
                                Reset Defaults
                              </SecondaryButton>
                              {isJeddAIEnabled &&
                                savedClassControlsById[clazz.id] && (
                                  <SecondaryButton
                                    onClick={() =>
                                      handleRemoveClassOverride(clazz.id)
                                    }
                                  >
                                    Remove override
                                  </SecondaryButton>
                                )}
                            </ActionRow>
                          </>
                        ) : (
                          <>
                            <SubTitle>
                              Using global settings for this class.
                            </SubTitle>
                            {isJeddAIEnabled && hasSavedOverride && (
                              <ActionRow>
                                <SecondaryButton
                                  onClick={() =>
                                    handleRemoveClassOverride(clazz.id)
                                  }
                                >
                                  Remove override
                                </SecondaryButton>
                              </ActionRow>
                            )}
                          </>
                        )}
                      </ClassCard>
                    );
                  })}
                </ClassesList>
              )}
            </Section>
          )}

          {isStudent && (
            <Section>
              <FeedbackAmountControl
                value={feedbackAmount}
                onChange={(value) => setFeedbackAmount(value)}
                label="My Drafts feedback amount"
                description="Choose how many in-text annotations and how much summary feedback you want in My Drafts."
              />
              {isSchoolStudent && (
                <SubTitle>
                  This preference only affects My Drafts and does not change
                  teacher-assigned tasks.
                </SubTitle>
              )}
              <ActionRow>
                <PrimaryButton onClick={handleSaveStudent}>
                  Save Preferences
                </PrimaryButton>
              </ActionRow>
            </Section>
          )}
        </Content>

        <SchoolPlanModal
          isOpen={isSchoolPlanModalOpen}
          onClose={() => setIsSchoolPlanModalOpen(false)}
          feature="Saving marking settings"
        />
        <StudentPlanUpgradePopup
          eventTrigger="advanced_marking"
          isOpen={isStudentUpgradeOpen}
          onClose={closeStudentUpgrade}
          onPlanSelect={closeStudentUpgrade}
          upgradeOptions={upgradeOptions}
          currentPlan={currentPlan}
          profile={profile}
        />
      </MainContainer>
    </OuterContainer>
  );
};

export default AdvancedMarkingControls;
